import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, QrCode, TrendingUp, Newspaper, 
    Tent, ClipboardList, Users, 
    Images, Camera, Globe, User, ShieldCheck, AlertTriangle,
    Settings, X, Sliders, BookOpen, Menu, ChevronRight
} from 'lucide-react';

const ALL_NAV_ITEMS = [
    { key: 'dashboard', path: '/', icon: <LayoutDashboard size={22} />, label: 'Dashboard', perm: null, bg: '#f1f5f9', color: '#475569', group: 'main' },
    { key: 'qr-okuyucu', path: '/qr-okuyucu', icon: <QrCode size={22} />, label: 'QR Yoklama', perm: 'qr-okuyucu', bg: '#eef2ff', color: '#6366f1', group: 'main' },
    { key: 'analiz', path: '/analiz', icon: <TrendingUp size={22} />, label: 'Raporlar', perm: 'analiz', bg: '#eff6ff', color: '#3b82f6', group: 'main' },
    { key: 'haberler', path: '/haberler', icon: <Newspaper size={22} />, label: 'Haberler', perm: 'haberler', bg: '#f5f3ff', color: '#8b5cf6', group: 'content' },
    { key: 'etkinlikler', path: '/etkinlikler', icon: <Tent size={22} />, label: 'Etkinlikler', perm: 'etkinlikler', bg: '#faf5ff', color: '#9333ea', group: 'content' },
    { key: 'basvurular', path: '/basvurular', icon: <ClipboardList size={22} />, label: 'Başvurular', perm: 'basvurular', bg: '#fefce8', color: '#ca8a04', group: 'content' },
    { key: 'tarifler', path: '/tarifler', icon: <Sliders size={22} />, label: 'Tarifler', perm: 'tarifler', bg: '#fffbeb', color: '#d97706', group: 'content' },
    { key: 'akademi', path: '/akademi', icon: <BookOpen size={22} />, label: 'Gastro Akademi', perm: 'tarifler', bg: '#ecfdf5', color: '#059669', group: 'content' },
    { key: 'uyeler', path: '/uyeler', icon: <Users size={22} />, label: 'Üyeler', perm: 'uyeler', bg: '#f0fdfa', color: '#0d9488', group: 'content' },
    { key: 'galeri', path: '/galeri', icon: <Images size={22} />, label: 'Galeri', perm: 'galeri', bg: '#fdf2f8', color: '#db2777', group: 'content' },
    { key: 'medya', path: '/medya', icon: <Camera size={22} />, label: 'Medya', perm: 'galeri', bg: '#f0f9ff', color: '#0284c7', group: 'content' },
    { key: 'site-yonetim', path: '/site-yonetim', icon: <Globe size={22} />, label: 'Site', perm: 'site-yonetim', bg: '#ecfdf5', color: '#059669', group: 'admin' },
    { key: 'kullanicilar', path: '/kullanicilar', icon: <User size={22} />, label: 'Kullanıcılar', perm: 'kullanicilar', bg: '#f5f3ff', color: '#6366f1', group: 'admin' },
    { key: 'denetim', path: '/denetim', icon: <ShieldCheck size={22} />, label: 'Denetim', perm: 'denetim', bg: '#fff1f2', color: '#e11d48', group: 'admin' },
    { key: 'hata-loglari', path: '/hata-loglari', icon: <AlertTriangle size={22} />, label: 'Hata Logları', perm: 'denetim', bg: '#fffbeb', color: '#d97706', group: 'admin' },
    { key: 'ayarlar', path: '/ayarlar', icon: <Settings size={22} />, label: 'Ayarlar', perm: null, bg: '#f1f5f9', color: '#475569', group: 'admin' },
];

/* ─── 4 sabit alt sekme (artık 5. sütun = Menü butonu) ─── */
const FIXED_TAB_KEYS = ['dashboard', 'etkinlikler', 'basvurular', 'ayarlar'];

function BottomNav({ hasPerm, isSuperAdmin, user }) {
    const location = useLocation();
    const [showSheet, setShowSheet] = useState(false);
    const sheetRef = useRef(null);
    const sheetBodyRef = useRef(null);
    const startY = useRef(0);
    const currentY = useRef(0);
    const isAtTop = useRef(true);

    // ── authorised items ──
    const allAuthorizedItems = useMemo(() => {
        return ALL_NAV_ITEMS.filter(item => {
            if (item.superOnly && !isSuperAdmin) return false;
            if (item.perm === null && !item.superOnly) return true;
            if (isSuperAdmin) return true;
            return hasPerm(item.perm);
        });
    }, [hasPerm, isSuperAdmin]);

    const hasExtraPages = allAuthorizedItems.length > 5;

    // ── 4 fixed tabs ──
    const fixedTabs = useMemo(() => {
        return FIXED_TAB_KEYS
            .map(key => allAuthorizedItems.find(i => i.key === key))
            .filter(Boolean);
    }, [allAuthorizedItems]);

    // ── sheet items (everything authorised) ──
    const sheetItems = allAuthorizedItems;

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    // close on navigate
    useEffect(() => { setShowSheet(false); }, [location.pathname]);

    // lock body scroll when sheet open
    useEffect(() => {
        document.body.style.overflow = showSheet ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [showSheet]);

    // ── touch-to-dismiss (swipe down) ──
    const handleTouchStart = useCallback((e) => {
        startY.current = e.touches[0].clientY;
        if (sheetBodyRef.current) {
            isAtTop.current = sheetBodyRef.current.scrollTop <= 0;
        } else {
            isAtTop.current = true;
        }
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!isAtTop.current) return;
        
        currentY.current = e.touches[0].clientY;
        const diff = currentY.current - startY.current;
        
        if (diff > 0 && sheetRef.current) {
            sheetRef.current.style.transform = `translateY(${diff}px)`;
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        const diff = currentY.current - startY.current;
        if (diff > 80) {
            setShowSheet(false);
        }
        if (sheetRef.current) {
            sheetRef.current.style.transform = '';
        }
        startY.current = 0;
        currentY.current = 0;
    }, []);

    // ── group sheet items ──
    const grouped = useMemo(() => {
        const groups = [
            { id: 'main', title: 'Genel', items: [] },
            { id: 'content', title: 'İçerik Yönetimi', items: [] },
            { id: 'admin', title: 'Yönetim & Sistem', items: [] },
        ];
        sheetItems.forEach(item => {
            const g = groups.find(gr => gr.id === item.group);
            if (g) g.items.push(item);
        });
        return groups.filter(g => g.items.length > 0);
    }, [sheetItems]);

    return (
        <>
            {/* ═══ OVERLAY ═══ */}
            <div
                className={`bn-overlay ${showSheet ? 'bn-overlay--visible' : ''}`}
                onClick={() => setShowSheet(false)}
            />

            {/* ═══ FULL-HEIGHT BOTTOM SHEET ═══ */}
            <div
                ref={sheetRef}
                className={`bn-sheet ${showSheet ? 'bn-sheet--open' : ''}`}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Drag Handle */}
                <div className="bn-sheet-handle">
                    <div className="bn-sheet-handle-bar" />
                </div>

                {/* Header */}
                <div className="bn-sheet-header">
                    <div>
                        <h2 className="bn-sheet-title">Yönetim Paneli</h2>
                        <p className="bn-sheet-subtitle">{allAuthorizedItems.length} modüle erişiminiz var</p>
                    </div>
                    <button className="bn-sheet-close" onClick={() => setShowSheet(false)}>
                        <X size={20} />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="bn-sheet-body" ref={sheetBodyRef}>
                    {grouped.map(group => (
                        <div key={group.id} className="bn-sheet-section">
                            <h3 className="bn-sheet-section-title">{group.title}</h3>
                            <div className="bn-sheet-list">
                                {group.items.map(item => (
                                    <Link
                                        key={item.key}
                                        to={item.path}
                                        className={`bn-sheet-row ${isActive(item.path) ? 'bn-sheet-row--active' : ''}`}
                                        onClick={() => setShowSheet(false)}
                                    >
                                        <div className="bn-sheet-row-icon" style={{ background: item.bg, color: item.color }}>
                                            {item.icon}
                                        </div>
                                        <span className="bn-sheet-row-label">{item.label}</span>
                                        <ChevronRight size={16} className="bn-sheet-row-chevron" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ═══ BOTTOM TAB BAR ═══ */}
            <nav className="bn-bar">
                {fixedTabs.map(item => (
                    <Link
                        key={item.key}
                        to={item.path}
                        className={`bn-tab ${isActive(item.path) ? 'bn-tab--active' : ''}`}
                    >
                        <span className="bn-tab-icon">{item.icon}</span>
                        <span className="bn-tab-label">{item.label}</span>
                    </Link>
                ))}

                {/* Menu Tab (5th) */}
                {hasExtraPages && (
                    <button
                        className={`bn-tab bn-tab-menu ${showSheet ? 'bn-tab--active' : ''}`}
                        onClick={() => setShowSheet(!showSheet)}
                    >
                        <span className="bn-tab-icon">
                            {showSheet ? <X size={22} /> : <Menu size={22} />}
                        </span>
                        <span className="bn-tab-label">Menü</span>
                    </button>
                )}
            </nav>

            {/* ═══ STYLES ═══ */}
            <style>{`
/* ── Overlay ── */
.bn-overlay {
    position: fixed; inset: 0; z-index: 998;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    opacity: 0; pointer-events: none;
    transition: opacity 0.3s ease;
}
.bn-overlay--visible {
    opacity: 1; pointer-events: auto;
}

/* ── Bottom Sheet ── */
.bn-sheet {
    position: fixed; left: 0; right: 0; bottom: 0;
    z-index: 999;
    background: #ffffff;
    border-radius: 24px 24px 0 0;
    transform: translateY(100%);
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: calc(85vh);
    display: flex; flex-direction: column;
    box-shadow: 0 -16px 48px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06);
    will-change: transform;
    -webkit-overflow-scrolling: touch;
}
.bn-sheet--open {
    transform: translateY(0);
}

/* Handle */
.bn-sheet-handle {
    display: flex; justify-content: center;
    padding: 10px 0 4px 0; flex-shrink: 0;
}
.bn-sheet-handle-bar {
    width: 40px; height: 5px;
    border-radius: 99px;
    background: #e2e8f0;
}

/* Header */
.bn-sheet-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 20px 14px 20px; flex-shrink: 0;
    border-bottom: 1px solid #f1f5f9;
}
.bn-sheet-title {
    margin: 0; font-size: 1.1rem; font-weight: 800;
    color: #0f172a; letter-spacing: -0.02em;
}
.bn-sheet-subtitle {
    margin: 2px 0 0 0; font-size: 0.78rem;
    color: #94a3b8; font-weight: 500;
}
.bn-sheet-close {
    width: 36px; height: 36px; border-radius: 12px;
    border: 1px solid #e2e8f0; background: #f8fafc;
    color: #64748b; display: flex; align-items: center;
    justify-content: center; cursor: pointer;
    transition: all 0.15s;
}
.bn-sheet-close:active {
    background: #e2e8f0; color: #0f172a;
}

/* Scrollable Body */
.bn-sheet-body {
    flex: 1; overflow-y: auto; padding: 8px 14px 28px 14px;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

/* Section */
.bn-sheet-section {
    margin-bottom: 10px;
}
.bn-sheet-section-title {
    margin: 0; padding: 10px 8px 6px 8px;
    font-size: 0.72rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    color: #94a3b8;
}

/* List Rows */
.bn-sheet-list {
    display: flex; flex-direction: column; gap: 2px;
}
.bn-sheet-row {
    display: flex; align-items: center; gap: 14px;
    padding: 12px 12px; border-radius: 16px;
    text-decoration: none; color: #1e293b;
    transition: background 0.15s;
    min-height: 56px; box-sizing: border-box;
}
.bn-sheet-row:active {
    background: #f1f5f9;
}
.bn-sheet-row--active {
    background: #f0fdf4;
}
.bn-sheet-row--active .bn-sheet-row-label {
    color: #059669; font-weight: 700;
}
.bn-sheet-row-icon {
    width: 44px; height: 44px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; transition: transform 0.15s;
}
.bn-sheet-row:active .bn-sheet-row-icon {
    transform: scale(0.92);
}
.bn-sheet-row-label {
    flex: 1; font-size: 0.94rem; font-weight: 600;
    color: #1e293b;
}
.bn-sheet-row-chevron {
    color: #cbd5e1; flex-shrink: 0;
}

/* ── Bottom Tab Bar ── */
.bn-bar {
    position: fixed; bottom: 0; left: 0; right: 0;
    height: var(--bottom-nav-height, 68px);
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
    box-shadow: 0 -4px 16px rgba(15,23,42,0.06);
    z-index: 1000;
    display: flex; align-items: stretch;
    padding-bottom: env(safe-area-inset-bottom, 0px);
}
.bn-tab {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 2px; padding: 6px 0;
    text-decoration: none; border: none; background: none;
    color: #94a3b8; font-size: 0.6rem; font-weight: 600;
    cursor: pointer; transition: color 0.15s;
    -webkit-tap-highlight-color: transparent;
    position: relative;
}
.bn-tab-icon {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 12px;
    transition: all 0.2s ease;
}
.bn-tab-label {
    white-space: nowrap; overflow: hidden;
    text-overflow: ellipsis; max-width: 68px;
}

/* Active State */
.bn-tab--active {
    color: #059669;
}
.bn-tab--active .bn-tab-icon {
    background: rgba(5, 150, 105, 0.1);
    color: #059669;
}
.bn-tab--active::before {
    content: ''; position: absolute; top: 0;
    left: 50%; transform: translateX(-50%);
    width: 24px; height: 3px;
    background: #059669; border-radius: 0 0 99px 99px;
}

/* Menu Tab (5th slot) special styling */
.bn-tab-menu .bn-tab-icon {
    border-radius: 12px;
}

/* ── DESKTOP: hide bottom bar ── */
@media (min-width: 769px) {
    .bn-bar, .bn-overlay, .bn-sheet { display: none !important; }
}
            `}</style>
        </>
    );
}

export { ALL_NAV_ITEMS };
export default BottomNav;
